import React, { useEffect, useState } from "react";
import { Download, Loader2, Mail, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getAdminSubscribers, adminSubscribersCsvUrl } from "@/lib/api";

/**
 * Owner-only "Blog email list" admin section.
 * Lists every signup from the /blog subscribe form with source attribution,
 * shows headline counts (24h / 7d / total), and offers a one-click CSV export
 * so the list can be imported into Mailchimp / Resend Audiences / etc.
 */
function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch { return iso; }
}

function sourceLabel(s) {
  if (!s) return "unknown";
  if (s === "blog-index") return "Blog index";
  if (s.startsWith("blog-post:")) return `Post · ${s.slice("blog-post:".length)}`;
  return s;
}

export default function SubscribersTracker() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  async function load() {
    setLoading(true);
    setErr(null);
    try {
      const d = await getAdminSubscribers({ limit: 200 });
      setData(d);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not load subscribers.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  return (
    <section
      className="rounded-2xl bg-white border border-black/5 bento-shadow p-6"
      data-testid="admin-subscribers-section"
    >
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="tag-label text-[#1E392A]/55 flex items-center gap-2">
            <Mail className="w-3.5 h-3.5" /> Blog email list
          </div>
          <h2 className="font-display text-2xl mt-1">Weekly GA4 tips subscribers</h2>
          <p className="text-sm text-[#1E392A]/65 mt-1">
            Every email captured from the /blog Subscribe form. Source attribution shows which post drove the signup.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            disabled={loading}
            data-testid="admin-subscribers-refresh"
            className="rounded-full"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
            Refresh
          </Button>
          <a
            href={adminSubscribersCsvUrl}
            data-testid="admin-subscribers-export-csv"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#1E392A] hover:bg-[#152b20] text-white text-sm font-medium px-4 h-9 transition-colors"
          >
            <Download className="w-4 h-4" /> Export CSV
          </a>
        </div>
      </div>

      {err && (
        <div className="mb-4 rounded-xl bg-[#FFE9DF] border border-[#E27D60]/30 text-[#a8492a] text-sm p-3">
          {err}
        </div>
      )}

      {loading && !data && (
        <div className="py-12 grid place-items-center text-[#1E392A]/55">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-3 gap-3 mb-5" data-testid="admin-subscribers-stats">
            <div className="rounded-xl bg-[#F7F5F0] p-4">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-[#1E392A]/55">Total</div>
              <div className="font-display text-3xl mt-1 leading-none">{data.total}</div>
            </div>
            <div className="rounded-xl bg-[#F7F5F0] p-4">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-[#1E392A]/55">Last 7 days</div>
              <div className="font-display text-3xl mt-1 leading-none">{data.recent_7d}</div>
            </div>
            <div className="rounded-xl bg-[#F7F5F0] p-4">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-[#1E392A]/55">Last 24h</div>
              <div className="font-display text-3xl mt-1 leading-none">{data.recent_24h}</div>
            </div>
          </div>

          {data.by_source?.length > 0 && (
            <div className="mb-5" data-testid="admin-subscribers-by-source">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-[#1E392A]/55 mb-2">
                Top sources
              </div>
              <div className="flex flex-wrap gap-1.5">
                {data.by_source.slice(0, 10).map((s, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center gap-1.5 text-xs bg-[#1E392A]/5 text-[#1E392A] rounded-full px-3 py-1"
                  >
                    {sourceLabel(s.source)}
                    <span className="font-semibold text-[#E27D60]">{s.count}</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-subscribers-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-[#1E392A]/55">
                  <th className="pb-3 font-semibold">Email</th>
                  <th className="pb-3 font-semibold">Source</th>
                  <th className="pb-3 font-semibold text-right">Submits</th>
                  <th className="pb-3 font-semibold text-right">Signed up</th>
                </tr>
              </thead>
              <tbody>
                {data.items.length === 0 && (
                  <tr><td colSpan="4" className="py-6 text-center text-[#1E392A]/55">No subscribers yet.</td></tr>
                )}
                {data.items.map((s, i) => (
                  <tr key={i} className="border-t border-black/5">
                    <td className="py-2.5 pr-3 font-mono text-[13px]">{s.email}</td>
                    <td className="py-2.5 pr-3 text-[#1E392A]/70 text-[13px]">{sourceLabel(s.source)}</td>
                    <td className="py-2.5 pr-3 text-right text-[#1E392A]/70">{s.submit_count || 1}</td>
                    <td className="py-2.5 text-right text-[#1E392A]/70">{fmtDate(s.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {data.total > data.items.length && (
            <div className="mt-3 text-xs text-[#1E392A]/55 text-right">
              Showing {data.items.length} of {data.total}. Use Export CSV for the full list.
            </div>
          )}
        </>
      )}
    </section>
  );
}
